<template>
  <div>
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push('/promotion')"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn
          style="float: right"
          class="capitalize ctrl-button right q-ml-md"
          :color="'ctrl-turquoise'"
          :disable="false"
          :label="`${$store.state.stringsStore.strings.action.save}`"
          @click="saveChanges()"
          icon-right="save"
        />
        <q-btn
          v-if="data.id"
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :label="$store.state.stringsStore.strings.action.delete"
          align="right"
          @click="deletePromotion()"
        />
      </div>
    </div>

    <!-- Left -->
    <div class="col-md-6 col-sm-12 col-xs-12">
      <div class="row q-mb-sm">
        <div class="full-width">
          <p class="card-title">Details</p>

          <div class="card-block">
            <div class="row">
              <div class="col-md-4">
                <q-input
                  class="q-mr-md"
                  color="primary"
                  v-model="data.name"
                  :float-label="getString('name')"
                />
              </div>
              <div class="col-md-4">
                <!-- Start date -->
                <q-datetime
                  format24h
                  :max="data.date.end"
                  class="q-mr-md"
                  :float-label="startDate"
                  v-model="data.date.start"
                />
              </div>
              <div class="col-md-4">
                <!-- End date -->
                <q-datetime
                  format24h
                  :min="data.date.start"
                  :float-label="endDate"
                  v-model="data.date.end"
                  clearable
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right -->
      <div class="col-md-12 col-sm-12 col-xs-12">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ 'Rules' }}
            </p>

            <q-btn
              class="ctrl-button capitalize q-mb-md"
              color="primary"
              @click="addNewRule"
              :label="getString('module.promotion.rule.new')"
            />

            <div
              class="card-block q-mb-sm"
              :class="{ 'rule-deleted': rule.deleted }"
              v-for="(rule, r) in combinedRules"
              :key="rule.newId || rule.id"
            >
              <div class="row">
                <!-- Rule type  -->
                <div class="col-md-4">
                  <q-field class="q-mb-md q-mr-md">
                    <q-select
                      :readonly="!!rule.id || rule.deleted"
                      :disable="rule.deleted"
                      :options="ruleSelect"
                      v-model="rule.type"
                      :float-label="getString('module.promotion.fields.type')"
                    />
                  </q-field>
                </div>

                <!-- Start date -->
                <div class="col-md-4">
                  <q-datetime
                    format24h
                    :max="rule.date.end"
                    class="q-mb-md q-mr-md"
                    :readonly="rule.deleted"
                    :disable="rule.deleted"
                    :float-label="startDate"
                    v-model="rule.date.start"
                  />
                </div>

                <!-- End date -->
                <div class="col-md-4">
                  <q-datetime
                    format24h
                    :min="rule.date.start"
                    class="full-width q-mb-md"
                    :readonly="rule.deleted"
                    :disable="rule.deleted"
                    :float-label="endDate"
                    v-model="rule.date.end"
                    clearable
                  />
                </div>
              </div>

              <div class="row">
                <chips-autocomplete
                  @change="updateAccounts(r, ...arguments)"
                  :readonly="rule.deleted"
                  :disable="rule.deleted"
                  :error="false"
                  :errorLabel="
                    $store.state.stringsStore.language === 'nl'
                      ? 'Dit veld is verplicht'
                      : 'This field is required'
                  "
                  class="q-pb-md full-width"
                  :selection="rule.accounts || []"
                  :name="'Accounts'"
                  :labelTerm="'name'"
                  :endpoint="'account'"
                  :resultLabel="'accounts'"
                />
              </div>

              <div
                class="row"
                v-if="['assortment_revenue', 'assortment_quantity'].includes(rule.type)"
              >
                <chips-autocomplete
                  @change="updateAssortment(r, ...arguments)"
                  :disable="rule.deleted"
                  :error="false"
                  :errorLabel="
                    $store.state.stringsStore.language === 'nl'
                      ? 'Dit veld is verplicht'
                      : 'This field is required'
                  "
                  class="q-pb-md full-width"
                  :selection="rule.assortments"
                  :name="'Assortment'"
                  :labelTerm="'description'"
                  :endpoint="'assortment'"
                  :resultLabel="'assortments'"
                />
              </div>

              <div class="row" v-if="['item_quantity'].includes(rule.type)">
                <chips-autocomplete
                  @change="updateItems(r, ...arguments)"
                  :readonly="rule.deleted"
                  :disable="rule.deleted"
                  :error="false"
                  :errorLabel="
                    $store.state.stringsStore.language === 'nl'
                      ? 'Dit veld is verplicht'
                      : 'This field is required'
                  "
                  class="q-pb-md full-width"
                  :selection="rule.items"
                  :name="'Items'"
                  :labelTerm="'code'"
                  :endpoint="'item'"
                  :resultLabel="'items'"
                />
              </div>

              <!-- Rewards (data) -->
              <q-field class="q-mb-md" v-if="rule.type == 'order'">
                <q-input
                  v-model="rule.order.min"
                  :float-label="'min'"
                  :readonly="rule.deleted"
                  type="number"
                  :disable="rule.deleted"
                />
                <q-input
                  v-model="rule.order.value"
                  :float-label="'value'"
                  :readonly="rule.deleted"
                  type="number"
                  :disable="rule.deleted"
                />
              </q-field>

              <div class="row" v-else v-for="(item, i) in rule.data" :key="`data-${i}`">
                <div class="col-md-4">
                  <q-input
                    class="q-mr-md"
                    v-model="item.min"
                    type="number"
                    :float-label="getString('module.promotion.fields.data.min')"
                    :readonly="rule.deleted"
                    :disable="rule.deleted"
                  />
                </div>
                <div class="col-md-4">
                  <q-input
                    v-model="item.value"
                    type="number"
                    :float-label="getString('module.promotion.fields.data.value')"
                    :readonly="rule.deleted"
                    :disable="rule.deleted"
                  />
                </div>
                <q-btn
                  :readonly="rule.deleted"
                  :disable="rule.deleted"
                  round
                  size="sm"
                  icon="clear"
                  color="negative"
                  class="q-ml-md q-mt-md capitalize"
                  @click="rule.data.splice(i, 1)"
                >
                </q-btn>
              </div>

              <div class="row">
                <q-btn
                  :readonly="rule.deleted"
                  :disable="rule.deleted"
                  v-if="rule.type !== null && rule.type !== 'order'"
                  :label="getString('module.promotion.fields.data.add')"
                  color="grey-5"
                  class="ctrl-button q-mt-md capitalize"
                  @click="rule.data.push({ min: null, value: null })"
                >
                </q-btn>
              </div>

              <q-btn
                v-if="!rule.deleted"
                class="ctrl-button capitalize q-mb-md q-mt-md"
                color="negative"
                @click="removeRule(r)"
                :label="getString('action.delete')"
              />

              <q-btn
                v-if="rule.deleted"
                class="ctrl-button capitalize q-mb-md q-mt-md"
                color="positive"
                @click="restoreRule(r)"
                label="restore"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import qs from 'qs';
import moment from 'moment';

// Mixins
import getString from '@/helpers/GetStrings';
import errorHandler from '@/mixins/detailViews/errorHandler';
import ChipsAutocomplete from '@/components/ChipsAutocomplete.vue';

export default {
  components: {
    ChipsAutocomplete,
  },
  mixins: [errorHandler],
  data() {
    return {
      // Name of the module defined in user rights. eg. blog for blog.edit, blog.add etc..
      permission: 'promotion',
      // Where data is fetched from
      endpoint: 'promotion',
      // The object key from the response object
      responseData: {
        single: 'promotion',
        multiple: 'promotions',
      },
      // Used for navigation on the front-end (matching the router)
      module: 'promotion',

      data: {
        name: '',
        date: {
          start: Date.now(),
          end: null,
          rules: [],
        },
      },

      newRules: [],
      rules: [],

      // When an existing rule is removed, it is added to this array for a delete on save.
      rulesRemoved: [],

      ruleSelect: [
        {
          label: getString('module.promotion.type.revenue'),
          value: 'revenue',
        },
        {
          label: getString('module.promotion.type.assortmentRevenue'),
          value: 'assortment_revenue',
        },
        {
          label: getString('module.promotion.type.assortmentQuantity'),
          value: 'assortment_quantity',
        },
        {
          label: getString('module.promotion.type.itemQuantity'),
          value: 'item_quantity',
        },
        {
          label: getString('module.promotion.type.order'),
          value: 'order',
        },
      ],
    };
  },

  methods: {
    getString,

    addNewRule() {
      this.newRules.unshift({
        type: null,
        date: { start: Date.now(), end: null },
        items: [],
        assortments: [],
        data: [],
        order: { min: 0, value: 0 },
        newId: (Math.random() + 1).toString(36).substring(7),
      });
    },

    fetchPromotion() {
      this.axios.get(`promotion/${this.$route.params.id}`).then(async res => {
        this.data = res.data.promotion;
        this.rules = await Promise.all(this.data.rules.map(rule => this.getRuleData(rule.id)));
      });
    },

    deletePromotion() {
      this.axios.delete(`promotion/${this.$route.params.id}`).then(() => {
        this.$router.push('/promotion');
      });
    },

    updateAssortment(ruleIndex, newVal) {
      this.combinedRules[ruleIndex].assortments = newVal;
    },

    updateItems(ruleIndex, newVal) {
      this.combinedRules[ruleIndex].items = newVal;
    },

    updateAccounts(ruleIndex, newVal) {
      this.combinedRules[ruleIndex].accounts = newVal;
    },

    requestParams() {
      return {};
    },

    removeRule(index) {
      const rule = this.combinedRules[index];
      // Move only existing rules to rulesRemoved array.
      if (rule.id) {
        this.rulesRemoved.push(rule);
        // Set deleted to true in the filtered array.
        this.$set(this.rules[index], 'deleted', true);
      } else {
        this.newRules.splice(index, 1);
      }
    },

    restoreRule(index) {
      const rule = this.rules[index];
      // Remove the rule from the rulesRemoved array.
      this.rulesRemoved = this.rulesRemoved.filter(item => item.id !== rule.id);

      this.$set(this.rules[index], 'deleted', false);
    },

    async getRuleData(ruleId) {
      const rule = await this.axios.get(`promotion/${this.$route.params.id}/rule/${ruleId}`);
      // Rule type order needs to be handled seperately

      if (rule.data.rule.type === 'order') {
        rule.data.rule.order = rule.data.rule.data;
      }
      return rule.data.rule;
    },

    /**
     * Saving a promotion requires saving the promotion itself as well as each rule within the promotion.
     */
    saveChanges() {
      if (this.$route.params.id) {
        this.saveExistingPromotion();
      } else {
        this.saveNewPromotion();
      }
    },

    cleanData(data) {
      Object.keys(data).forEach(key => {
        if (!data[key] && data[key] !== false) {
          delete data[key];
        }
      });
      return data;
    },

    saveExistingPromotion() {
      let data = {
        name: this.data.name,
        date_start: moment(this.data.date.start).format('YYYY-MM-DD'),
        date_end: this.data.date.end ? moment(this.data.date.end).format('YYYY-MM-DD') : null,
      };
      data = this.cleanData(data);
      this.axios.put(`promotion/${this.$route.params.id}`, qs.stringify(data)).then(async () => {
        // Save all rules.
        await Promise.all(
          this.combinedRules.map(rule => {
            // Save all existing rules.
            if (rule.id) {
              if (!rule.deleted) {
                return this.saveExistingRule(rule);
              }
              return true;
            }
            // Save all new rules.
            return this.saveNewRule(rule);
          }),
        )
          .then(() => {
            this.newRules = [];
          })
          .catch((err) => {
            // Handle the error (handleError mixin)
            this.handleError(err);
          });

        // Apply deletes
        await Promise.all(
          this.rulesRemoved.map((rule) => this.axios.delete(`promotion/${this.$route.params.id}/rule/${rule.id}`))
        );
        this.$q.notify({
          message: getString('module.promotion.edit.save.success'),
          type: 'positive',
          timeout: 1200,
          position: 'top-right',
        });
        this.fetchPromotion();
      });
    },

    // checkRule(rule) {
    //   // Check mandatory fields for all rules
    //   if (!rule.type || !rule.data) {}

    // },

    // checkScale(scale) {

    // },

    saveNewPromotion() {
      let data = {
        name: this.data.name,
        date_start: moment(this.data.date.start).format('YYYY-MM-DD'),
        date_end: this.data.date.end ? moment(this.data.date.end).format('YYYY-MM-DD') : null,
      };
      data = this.cleanData(data);
      this.axios
        .post('promotion', qs.stringify(data))
        .then((response) => {
          // Redirect to detail page of the newly created item
          if (response.headers && response.headers.location) {
            const location = response.headers.location.replace('/api', '');
            this.$router.push(location);
          }
        })
        .then(async () => {
          // Post successful, check id
          if (this.$route.params.id) {
            // id availible, post all rules.
            await Promise.all(this.rules.map(rule => this.saveNewRule(rule)))
              .then(() => {
                this.newRules = [];
              })
              .catch((err) => {
                // Handle the error (handleError mixin)
                this.handleError(err);
              });

            // Successfully saved all new rules.
            this.$q.notify({
              message: getString('module.promotion.edit.save.success'),
              type: 'positive',
              timeout: 1200,
              position: 'top-right',
            });
          }
        })
        .catch((err) => {
          // Handle the error (handleError mixin)
          this.handleError(err);
        });
    },

    saveNewRule(rule) {
      let ruleData = {
        type: rule.type,
        data: rule.type === 'order' ? JSON.stringify(rule.order) : JSON.stringify(rule.data),
        date_start: rule.date.start ? moment(rule.date.start).format('YYYY-MM-DD') : null,
        date_end: rule.date.end ? moment(rule.date.end).format('YYYY-MM-DD') : null,
        assortment: rule.assortments ? rule.assortments.map(assortment => assortment.id) : null,
        item: rule.items ? rule.items.map(item => item.id) : null,
      };

      ruleData = this.cleanData(ruleData);
      return this.axios.post(
        `promotion/${this.$route.params.id}/rule`,
        qs.stringify(ruleData, { plainObjects: true }),
      );
    },

    saveExistingRule(rule) {
      let ruleData = {
        // type: rule.type,
        data: rule.type === 'order' ? JSON.stringify(rule.order) : JSON.stringify(rule.data),
        date_start: rule.date.start ? moment(rule.date.start).format('YYYY-MM-DD') : null,
        date_end: rule.date.end ? moment(rule.date.end).format('YYYY-MM-DD') : null,
        assortment: rule.assortments ? rule.assortments.map(assortment => assortment.id) : null,
        item: rule.items ? rule.items.map(item => item.id) : null,
        account: rule.accounts ? rule.accounts.map(account => account.id) : null,
      };

      ruleData = this.cleanData(ruleData);
      return this.axios.put(
        `promotion/${this.$route.params.id}/rule/${rule.id}`,
        qs.stringify(ruleData, { plainObjects: true }),
      );
    },
  },

  computed: {
    combinedRules() {
      return [...this.newRules, ...this.rules];
    },

    startDate() {
      return this.$store.state.stringsStore.language === 'nl' ? 'Startdatum' : 'Start date';
    },

    endDate() {
      return this.$store.state.stringsStore.language === 'nl' ? 'Einddatum' : 'End date';
    },
  },

  mounted() {
    if (this.$route.params.id) {
      this.fetchPromotion();
    }
  },
};
</script>

<style lang="stylus">
.card-block.rule-deleted
    background rgba(255, 255, 255, 0.5)
</style>
